import { UTCDate } from '@date-fns/utc';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useClient } from 'urql';

import { hasEntitySummaryTab, useAuthStore } from '@revelio/auth';
import { FeatureFlag } from '@revelio/core';
import {
  COMPOSITION_HEADCOUNT_ONLY,
  type CustomRoleTaxonomySelection,
  Dimension1,
  ENTITY_V2_QUERY,
  EntityV2Type,
  GET_SAVED_SETS,
  MonthWeekIndexQuery,
  PAY_V2_TOPGEOS_QUERY,
  POSTING_GET_DATA,
  POSTING_SOURCE_IDS,
  PositionCategoryKind,
  PositionsEntityKind,
  RICS_SELECTION_LIST,
  ROLE_TAXONOMY_VERSIONS_QUERY,
  SENTIMENT_SNAPSHOT,
  SKILL_TAXONOMIES_VERSIONS_QUERY,
  SKILL_TAXONOMY_SECONDARY_SELECTION_LIST_QUERY,
  TALENT_DISCOVERY_MAP_DATA_V3,
  TAXONOMY_LATEST_VERSION_QUERY,
  TAXONOMY_LIST_QUERY,
  Tab,
  TaxonomyType,
} from '@revelio/data-access';

import { fetchRoleSelectionListForTaxonomy } from '../../shared/selection-lists/api/fetchRoleSelectionLists';
import { useUnleashFlag } from '../../shared/unleash-flags/use-unleash-flag';
import { getAllPreferences } from '../../shared/user-preferences/api/user-preferences-api';
import {
  PreferenceScope,
  UserPreferenceKey,
  type UserPreferences,
} from '../../shared/user-preferences/types';
import { getDefaultPreferences } from '../../shared/user-preferences/utils/preference-defaults';
import { PreferenceBrowserStorage } from '../../shared/user-preferences/utils/preference-session-storage';
import { getRoleTaxonomyId } from '../CustomDash/components/DataProvider/talentDiscovery/roles';
import { getSkillTaxonomyId } from '../CustomDash/components/DataProvider/talentDiscovery/skills';
import { createNewFilterState } from '../CustomDash/filters/engine/filterGroupConsistency';
import { FilterId, FilterType } from '../CustomDash/filters/types/filters';
import { getFormattedDateString } from '../CustomDash/filters/utils/dateUtils';
import { getActiveSelections } from '../CustomDash/filters/utils/primaryFilterUtils';
import { GLOBAL_MIN_START_DATE } from '../CustomDash/filters/utils/providerDateOverrides';

/**
 * Prefetch overview page data while the user is on the landing page.
 *
 * Uses the same `createNewFilterState` code path as the overview page
 * (with selectionDefault: 1) so that entity selection, adaptive taxonomy,
 * and filter construction stay aligned.
 *
 * Fetches a lightweight rics-only selection list query in parallel with
 * user preferences and system date bounds, so taxonomy computation is not
 * blocked by the full selection list query.
 *
 * Priority order:
 * 1. Fetch rics_k400, user preferences, and MonthWeekIndexQuery in parallel
 * 2. Compute adaptive taxonomy via createNewFilterState
 * 3. Fire chart queries for Company Overview (most common navigation path)
 * 4. Prefetch taxonomy-specific role/skill selection lists (lower priority)
 */
export const usePrefetchOverviewData = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const hasPrefetched = useRef(false);
  const useReferenceApi = useUnleashFlag(FeatureFlag.ReferenceApi);

  useEffect(() => {
    if (hasPrefetched.current) return;
    if (!currentUser) return; // don't mark as prefetched; allow retry

    // Skip prefetch entirely if the user doesn't have access to the overview page
    const userTabs = currentUser.tabs ?? [];
    if (userTabs.length > 0 && !hasEntitySummaryTab(userTabs)) {
      hasPrefetched.current = true;
      return;
    }

    hasPrefetched.current = true;

    const prefetch = async () => {
      // Fire no-variable queries immediately (no dependencies).
      // Best-effort prefetches not awaited below — swallow rejections.
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      const swallowError = () => {};
      // Skip global error handling (error dialogs + Kibana logs) for all
      // prefetch queries — failures are expected and the overview page will
      // re-fetch its own data.
      const prefetchContext = { skipGlobalErrorHandling: true };
      if (useReferenceApi) {
        client
          .query(
            TAXONOMY_LIST_QUERY,
            { taxonomyType: TaxonomyType.Role },
            prefetchContext
          )
          .toPromise()
          .catch(swallowError);
        client
          .query(
            TAXONOMY_LATEST_VERSION_QUERY,
            { taxonomyType: TaxonomyType.Role },
            prefetchContext
          )
          .toPromise()
          .catch(swallowError);
      } else {
        client
          .query(ROLE_TAXONOMY_VERSIONS_QUERY, {}, prefetchContext)
          .toPromise()
          .catch(swallowError);
      }
      client
        .query(SKILL_TAXONOMIES_VERSIONS_QUERY, {}, prefetchContext)
        .toPromise()
        .catch(swallowError);
      client
        .query(GET_SAVED_SETS, {}, prefetchContext)
        .toPromise()
        .catch(swallowError);

      // Fetch rics, preferences, and date index in parallel
      const [{ data: ricsData }, allPrefs, { data: dateIndexData }] =
        await Promise.all([
          client.query(RICS_SELECTION_LIST, {}, prefetchContext).toPromise(),
          queryClient.fetchQuery({
            queryKey: ['user-preferences'],
            queryFn: () => getAllPreferences(),
          }),
          client.query(MonthWeekIndexQuery, {}, prefetchContext).toPromise(),
        ]);

      // Write preferences to session storage so FilterProvider can read them
      // synchronously when it initializes. Without this, the react-query cache
      // being pre-populated causes useUserPreferences to skip the loading gate,
      // but the useEffect that writes to PreferenceBrowserStorage hasn't run yet.
      if (allPrefs) {
        Object.entries(allPrefs).forEach(([scopeKey, prefs]) => {
          const scope = scopeKey as PreferenceScope;
          const existing = PreferenceBrowserStorage.get(scope);
          PreferenceBrowserStorage.set(scope, {
            ...prefs,
            ...existing,
          } as UserPreferences);
        });
      }

      const ricsK400Raw = ricsData?.selectionList?.rics_k400;
      const endMonth = dateIndexData?.index?.endMonth;
      const maxWeek = dateIndexData?.index?.maxWeek;
      if (!ricsK400Raw?.length || !endMonth || !maxWeek) return;

      // Filter out nulls to match the type expected by createNewFilterState
      const ricsK400 = ricsK400Raw.filter(
        (item): item is NonNullable<typeof item> => item !== null
      );

      // Merge preferences: defaults + global + company scope
      const defaults = getDefaultPreferences(PreferenceScope.COMPANY);
      const prefs: UserPreferences = {
        ...defaults,
        ...allPrefs[PreferenceScope.GLOBAL],
        ...allPrefs[PreferenceScope.COMPANY],
      } as UserPreferences;
      // Company overview uses 'posting-provider-range' preset:
      //   start = GLOBAL_MIN_START_DATE, end = systemMax
      // Use UTCDate + getFormattedDateString to match overview page date processing
      const startDateMonth = getFormattedDateString(
        new UTCDate(GLOBAL_MIN_START_DATE),
        'month'
      );
      const endDateMonth = getFormattedDateString(
        new UTCDate(endMonth),
        'month'
      );
      const startDateWeek = getFormattedDateString(
        new UTCDate(GLOBAL_MIN_START_DATE),
        'week'
      );
      const endDateWeek = getFormattedDateString(new UTCDate(maxWeek), 'week');
      // Pay uses viewGranularity='week' which resolves systemMax from maxWeek,
      // then formats it as 'month' (yyyy-MM). This means pay's end_date is
      // the month derived from maxWeek, not endMonth.
      const endDateMonthFromWeek = getFormattedDateString(
        new UTCDate(maxWeek),
        'month'
      );

      // Resolve default selections from preferences (or hardcoded defaults)
      const userDefaultSelections =
        prefs[UserPreferenceKey.DefaultFilters] ?? [];

      // Build filter state using the same code path as the overview page.
      // selectionDefault: 1 matches company-overview-v2 template config.
      const filterState = createNewFilterState({
        primaryFilterId: FilterId.COMPANY,
        pageId: 'company-overview',
        selectionDefault: 1,
        ricsK400List: ricsK400,
        defaultSelections:
          userDefaultSelections.length > 0
            ? { [FilterId.COMPANY]: userDefaultSelections }
            : undefined,
      });

      const filterMap = filterState.groupMap.get(FilterId.COMPANY);
      if (!filterMap) return;

      // Extract the active company selection (single entity for overview)
      const companyFilter = filterMap.get(FilterId.COMPANY);
      if (!companyFilter || companyFilter.type !== FilterType.TREE) return;

      const activeSelections = getActiveSelections(
        companyFilter.primarySelections ?? []
      );
      if (activeSelections.length === 0) return;

      // Convert filter map to array for getRoleTaxonomyId / getSkillTaxonomyId
      const filtersArray = Array.from(filterMap.values());

      // Use the same functions the TD map data provider uses
      const rolesTax = getRoleTaxonomyId(filtersArray);
      const skillsTax = getSkillTaxonomyId(filtersArray);

      // Apply preference-controlled filter values
      const contingentWorkers =
        prefs[UserPreferenceKey.ContingentWorkers] ?? false;

      // Build entities from active selections
      const entities = activeSelections.map((sel) => ({
        EntType: PositionsEntityKind.Company,
        EntID: parseInt(sel.id.split('_').slice(-1)[0] ?? '0', 10),
      }));

      // Extract taxonomy values for non-TD queries
      const roleTaxonomyFilter = filterMap.get(FilterId.ROLE_TAXONOMY);
      const roleTaxonomy: CustomRoleTaxonomySelection =
        roleTaxonomyFilter?.type === FilterType.VALUE
          ? (roleTaxonomyFilter.value as CustomRoleTaxonomySelection)
          : { default: true };

      const skillTaxonomyFilter = filterMap.get(FilterId.SKILL_TAXONOMY);
      const skillTaxonomyId =
        skillTaxonomyFilter?.type === FilterType.VALUE
          ? ((skillTaxonomyFilter.value as { taxonomyId?: number })
              ?.taxonomyId ?? 0)
          : 0;

      // Shared filter base
      const baseFilters = {
        raw: false,
        contingent_workers: contingentWorkers,
        custom_role: { taxonomyId: roleTaxonomy },
        custom_skill: { taxonomy_id: skillTaxonomyId },
      };

      // Fire queries — results land in URQL cache for instant render on overview page

      // Gate chart prefetches on user tab permissions to avoid firing queries
      // the user isn't authorized for. Mirrors RequireAuth tab-check behavior:
      // empty tabs = unrestricted; non-empty = only listed tabs.
      const canPrefetchTab = (tab: Tab) =>
        userTabs.length === 0 || userTabs.includes(tab);

      // Talent Discovery Map (highest priority)
      const companyExprNodes = activeSelections.map((sel) => ({
        val: {
          pos_attribute: {
            kind: PositionCategoryKind.CompanyId,
            id: parseInt(sel.id.split('_').slice(-1)[0] ?? '0', 10),
            curr: true,
          },
        },
      }));

      const firstNode = companyExprNodes[0];
      if (!firstNode) return;

      const companyExprNode =
        companyExprNodes.length === 1
          ? firstNode
          : { or: { children: companyExprNodes } };

      // All chart prefetches are best-effort — catch individually since they
      // are not awaited and would otherwise produce unhandled rejections.
      if (canPrefetchTab(Tab.TalentDiscovery)) {
        client
          .query(
            TALENT_DISCOVERY_MAP_DATA_V3,
            {
              query: {
                filters: {
                  expr: {
                    user_value: {
                      child: companyExprNode,
                    },
                  },
                  roles_tax: rolesTax,
                  skills_tax: skillsTax,
                },
                pagination: {
                  page_num: 1,
                  page_size: 5000,
                },
              },
            },
            prefetchContext
          )
          .toPromise()
          .catch(swallowError);
      }

      // Composition (headcount only)
      if (canPrefetchTab(Tab.Overview)) {
        client
          .query(
            COMPOSITION_HEADCOUNT_ONLY,
            {
              entities,
              filters: {
                ...baseFilters,
                start_date: startDateMonth,
                end_date: endDateMonth,
              },
            },
            prefetchContext
          )
          .toPromise()
          .catch(swallowError);
      }

      // Pay (top geos) — uses week granularity, so systemMax = maxWeek formatted as month
      if (canPrefetchTab(Tab.Compensation)) {
        client
          .query(
            PAY_V2_TOPGEOS_QUERY,
            {
              req: {
                entities,
                filters: {
                  ...baseFilters,
                  start_date: startDateMonth,
                  end_date: endDateMonthFromWeek,
                },
              },
            },
            prefetchContext
          )
          .toPromise()
          .catch(swallowError);
      }

      // Sentiment (snapshot — overview uses isSnapshot:true → both dates = max)
      if (canPrefetchTab(Tab.Sentiment)) {
        client
          .query(
            SENTIMENT_SNAPSHOT,
            {
              dim1: Dimension1.Company,
              filters: {
                company: activeSelections.map((sel) =>
                  parseInt(sel.id.split('_').slice(-1)[0] ?? '0', 10)
                ),
                custom_role: { taxonomyId: roleTaxonomy },
                start_date: endDateMonth,
                end_date: endDateMonth,
              },
            },
            prefetchContext
          )
          .toPromise()
          .catch(swallowError);
      }

      // Postings
      if (canPrefetchTab(Tab.Posting)) {
        client
          .query(
            POSTING_GET_DATA,
            {
              dim1: Dimension1.Company,
              filters: {
                company: activeSelections.map((sel) =>
                  parseInt(sel.id.split('_').slice(-1)[0] ?? '0', 10)
                ),
                custom_role: { taxonomyId: roleTaxonomy },
                custom_skill: { taxonomy_id: skillTaxonomyId },
                provider: [POSTING_SOURCE_IDS.unified],
                metric_mode: 'posting',
                start_date: startDateWeek,
                end_date: endDateWeek,
              },
            },
            prefetchContext
          )
          .toPromise()
          .catch(swallowError);
      }

      // Entity summary (description + competitors)
      const firstEntity = entities[0];
      if (
        firstEntity &&
        (userTabs.length === 0 || hasEntitySummaryTab(userTabs))
      ) {
        client
          .query(
            ENTITY_V2_QUERY,
            {
              req: {
                entID: firstEntity.EntID,
                entType: EntityV2Type.Company,
                numCompetitors: 7,
              },
            },
            prefetchContext
          )
          .toPromise()
          .catch(swallowError);
      }

      // Prefetch taxonomy-specific selection lists (lower priority than chart data)
      // Role selection list with correct adaptive taxonomy
      fetchRoleSelectionListForTaxonomy({
        taxonomySelection: roleTaxonomy,
        gqlClient: client,
        context: prefetchContext,
      });

      // Skill taxonomy selection list with correct adaptive taxonomy
      if (skillTaxonomyId > 0) {
        client
          .query(
            SKILL_TAXONOMY_SECONDARY_SELECTION_LIST_QUERY,
            {
              taxonomy_id: skillTaxonomyId,
              l0: 1,
              l1: 2,
              l2: 3,
              l3: 4,
              l4: 5,
            },
            { ...prefetchContext, priority: 'low' }
          )
          .toPromise()
          .catch(swallowError);
      }
    };

    prefetch().catch(() => {
      // Prefetch is best-effort — if any query fails (e.g. missing auth
      // token on initial render), the overview page will fetch its own data.
    });
  }, [client, currentUser, queryClient, useReferenceApi]);
};
